import { useCallback, useMemo } from 'react';

import { useDashboardEditActions } from '@/features/Dashboard/pages/Edit/hooks/useDashboardEditActions';
import { useMoveMenuCallbacks } from '@/features/Dashboard/pages/Edit/hooks/useMoveMenuCallbacks';
import { getTabNameError } from '@/features/Dashboard/pages/Edit/model/utils/invariants';
import { buildDashboardFromDraft } from '@/features/Dashboard/pages/Edit/model/utils/mapping';
import { useDashboard } from '@/features/Dashboard/api';
import { useAppSelector } from '@/hooks/redux-hooks';

import { DashboardTargetMenu } from './menus';
import {
  selectDashboardName,
  selectDraftTabs,
  selectInitialDashboardName,
  selectOtherDashboardDrafts,
  selectTabDrafts
} from '../model/dashboardEdit.selectors';

type MoveTabToDashboardMenuProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  tabId: string;
  tabName: string;
  disableMove?: boolean;
  onClose: () => void;
  onMoved?: () => void;
  /**
   * Callback to remove the tab from the local draftTabs list in useDashboardTabs hook.
   * Since draftTabs is managed by the hook in Edit/index.tsx, we need this callback
   * to sync the local state after the context handles the draft data.
   */
  onRemoveTab?: (tabId: string) => void;
};

/**
 * Menu for moving a tab (page) to another dashboard.
 *
 * Uses context method exclusively:
 * - moveTabToDashboard: handles all validation and draft updates
 *
 * All moves work on drafts only - no network requests.
 * Uses 'dashboard-only' variant (no tab submenu) since we're moving entire tabs.
 *
 * Move is disabled when:
 * - disableMove prop is true
 * - tabName fails validation (empty, too long, invalid characters, reserved name, etc.)
 */
const MoveTabToDashboardMenu = ({
  anchorEl,
  disableMove,
  onClose,
  onMoved,
  onRemoveTab,
  open,
  tabId,
  tabName
}: MoveTabToDashboardMenuProps) => {
  const draftDashboardName = useAppSelector(selectDashboardName);
  const initialDashboardName = useAppSelector(selectInitialDashboardName);
  const draftTabs = useAppSelector(selectDraftTabs);
  const tabDrafts = useAppSelector(selectTabDrafts);
  const otherDashboardDrafts = useAppSelector(selectOtherDashboardDrafts);
  const actions = useDashboardEditActions();
  const { data: allDashboards } = useDashboard();

  const effectiveCurrentDashboardName = draftDashboardName || initialDashboardName;

  const { handleCreateDashboard, handleError, handleSuccess } = useMoveMenuCallbacks({
    createDashboardDraft: actions.createUniqueDashboardDraft
  });

  const tabNameError = useMemo(() => {
    // Validate tab name using the same schema as EditableTabLabel
    // Pass empty array for existingNames since we can't check duplicates
    // in the target dashboard without knowing which one the user will select
    return getTabNameError(tabName, []);
  }, [tabName]);

  const isMoveDisabled = disableMove || !!tabNameError;

  const dashboardNamesList = useMemo(() => {
    const names = new Set(Object.keys(allDashboards || {}));

    // Add all dashboards that have drafts in session
    Object.keys(otherDashboardDrafts).forEach(name => names.add(name));
    if (draftDashboardName) names.add(draftDashboardName);
    if (initialDashboardName) names.add(initialDashboardName);

    // If draft name differs from initial, remove the old name
    if (draftDashboardName && draftDashboardName !== initialDashboardName) {
      names.delete(initialDashboardName);
    }

    // For 'dashboard-only' variant, exclude current dashboard
    names.delete(effectiveCurrentDashboardName || '');
    if (initialDashboardName) names.delete(initialDashboardName);

    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [
    allDashboards,
    otherDashboardDrafts,
    draftDashboardName,
    initialDashboardName,
    effectiveCurrentDashboardName
  ]);

  const getDashboardTabs = useCallback(
    (dashboardName: string) => {
      const isMainDraft =
        dashboardName === draftDashboardName ||
        (initialDashboardName && dashboardName === initialDashboardName);

      let tabs: Record<string, any> | null;
      if (isMainDraft) {
        tabs = buildDashboardFromDraft(draftTabs, tabDrafts);
      } else if (otherDashboardDrafts[dashboardName]) {
        const draft = otherDashboardDrafts[dashboardName];
        tabs = buildDashboardFromDraft(draft.draftTabs, draft.tabDrafts);
      } else {
        tabs = allDashboards?.[dashboardName] || null;
      }

      if (!tabs) return null;

      return Object.entries(tabs).map(([tabName, tabValue]) => {
        const serverTabId = tabValue?.id;

        let tabId: string | null;
        if (isMainDraft) {
          const draftTab = draftTabs.find(t => t.id === serverTabId || t.name === tabName);
          tabId = draftTab?.id || null;
        } else if (otherDashboardDrafts[dashboardName]) {
          const draft = otherDashboardDrafts[dashboardName];
          const draftTab = draft.draftTabs.find(t => t.id === serverTabId || t.name === tabName);
          tabId = draftTab?.id || null;
        } else {
          tabId = serverTabId || null;
        }

        return { tabName, tabId: tabId || tabName };
      });
    },
    [
      draftDashboardName,
      initialDashboardName,
      draftTabs,
      tabDrafts,
      otherDashboardDrafts,
      allDashboards
    ]
  );

  /**
   * Handle selecting a target dashboard.
   */
  const handleSelectDashboard = useCallback(
    (targetDashboardName: string) => {
      if (isMoveDisabled) {
        return;
      }

      actions.moveTabToOtherDashboard(targetDashboardName, tabId, tabName);

      handleSuccess(`Page moved to "${targetDashboardName}" as "${tabName}"!`);
      onRemoveTab?.(tabId);
      onMoved?.();
      onClose();
    },
    [isMoveDisabled, actions, tabId, tabName, handleSuccess, onRemoveTab, onMoved, onClose]
  );

  return (
    <DashboardTargetMenu
      anchorEl={anchorEl}
      dashboards={dashboardNamesList}
      disabled={isMoveDisabled}
      emptyMessage='No other dashboards'
      getTabs={getDashboardTabs}
      open={open}
      showChevron={false}
      variant='dashboard-only'
      onClose={onClose}
      onCreateDashboard={handleCreateDashboard}
      onError={handleError}
      onSelectDashboard={handleSelectDashboard}
    />
  );
};

export default MoveTabToDashboardMenu;
